import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Container,
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  Snackbar,
  Avatar,
  IconButton,
  CircularProgress,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Chip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PhotoCamera as PhotoCameraIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import axios from 'axios';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://soldikeeper-backend-production.up.railway.app/api';

const ProfileSettings = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    timezone: user?.timezone || 'UTC',
    locale: user?.locale || 'en'
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [errors, setErrors] = useState({});
  
  // Avatar upload states
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  // Avatar upload handlers
  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const validateAndProcessFile = (file) => {
    if (!file) return false;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setSnackbar({
        open: true,
        message: t('profile.invalidFileType', 'Please select an image file (JPG, PNG, GIF, or WebP)'),
        severity: 'error'
      });
      return false;
    }

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      setSnackbar({
        open: true,
        message: t('profile.fileTooLarge', 'Image must be under 10MB'),
        severity: 'error'
      });
      return false;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result);
      setSelectedFile(file);
      setUploadDialog(true);
    };
    reader.readAsDataURL(file);
    return true;
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    validateAndProcessFile(file);
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    validateAndProcessFile(file);
  };

  const handleUploadConfirm = async () => {
    console.log('🚀 Avatar upload started, file:', selectedFile);
    if (!selectedFile) {
      console.warn('⚠️ No file selected');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('avatar', selectedFile);
      
      console.log('📤 Uploading to:', `${API_BASE_URL}/user-preferences/avatar/upload`);
      console.log('📦 FormData contains:', selectedFile.name, selectedFile.size, 'bytes');

      const response = await axios.post(
        `${API_BASE_URL}/user-preferences/avatar/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(`⏫ Upload progress: ${percentCompleted}%`);
            setUploadProgress(percentCompleted);
          }
        }
      );
      
      console.log('✅ Upload response:', response.data);

      // Update user context with new avatar URL
      if (response.data.avatarUrl && setUser) {
        setUser({
          ...user,
          avatar: response.data.avatarUrl,
          avatarUrl: response.data.avatarUrl
        });
      }

      setSnackbar({
        open: true,
        message: t('profile.avatarUploadSuccess', 'Profile picture updated successfully!'),
        severity: 'success'
      });

      setUploadDialog(false);
      setAvatarPreview(null);
      setSelectedFile(null);

    } catch (error) {
      console.error('Avatar upload error:', error);
      
      let errorMessage = t('profile.avatarUploadError', 'Failed to upload profile picture');
      
      if (error.response?.status === 413) {
        errorMessage = t('profile.fileTooLarge', 'Image must be under 10MB');
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message.includes('Network')) {
        errorMessage = t('profile.networkError', 'Network error. Please check your connection and try again.');
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleUploadCancel = () => {
    setUploadDialog(false);
    setAvatarPreview(null);
    setSelectedFile(null);
    setUploadProgress(0);
    // Clear the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveAvatar = async () => {
    if (!window.confirm(t('profile.confirmRemoveAvatar', 'Are you sure you want to remove your profile picture?'))) {
      return;
    }

    setIsUploading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/user-preferences/avatar`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      // Update user context to remove avatar
      if (setUser) {
        setUser({
          ...user,
          avatar: null,
          avatarUrl: null
        });
      }

      setSnackbar({
        open: true,
        message: t('profile.avatarRemoved', 'Profile picture removed successfully'),
        severity: 'success'
      });

    } catch (error) {
      console.error('Avatar removal error:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || t('profile.avatarRemoveError', 'Failed to remove profile picture'),
        severity: 'error'
      });
    } finally {
      setIsUploading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name || formData.name.trim().length === 0) {
      newErrors.name = t('profile.nameRequired', 'Name is required');
    }
    
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('profile.emailInvalid', 'Valid email is required');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE_URL}/user-preferences/profile`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      // Update user data in context
      if (setUser && response.data.user) {
        setUser(response.data.user);
      }

      // Sync language if locale was changed
      if (formData.locale && formData.locale !== i18n.language) {
        i18n.changeLanguage(formData.locale);
        localStorage.setItem('soldikeeper-language', formData.locale);
      }

      setSnackbar({
        open: true,
        message: t('profile.updateSuccess', 'Profile updated successfully'),
        severity: 'success'
      });

    } catch (error) {
      console.error('Error updating profile:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || t('profile.updateError', 'Failed to update profile'),
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const timezones = [
    { value: 'UTC', label: 'UTC' },
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'Europe/London', label: 'London (GMT)' },
    { value: 'Europe/Paris', label: 'Paris (CET)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEST)' }
  ];

  const locales = [
    { value: 'en', label: 'English' },
    { value: 'fr', label: 'Français' },
    { value: 'de', label: 'Deutsch' },
    { value: 'es', label: 'Español' },
    { value: 'it', label: 'Italiano' },
    { value: 'pt', label: 'Português' }
  ];

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/settings')} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            {t('profile.title', 'Profile Settings')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('profile.subtitle', 'Update your personal information')}
          </Typography>
        </Box>
      </Box>

      <Paper elevation={2} sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          {/* Avatar Section */}
          <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box 
              sx={{ 
                position: 'relative',
                cursor: 'pointer',
                transition: 'all 0.3s',
                transform: isDragging ? 'scale(1.05)' : 'scale(1)',
                '&:hover': {
                  transform: 'scale(1.02)'
                }
              }}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={handleAvatarClick}
            >
              <Avatar
                src={user?.avatar || user?.avatarUrl}
                sx={{
                  width: 100,
                  height: 100,
                  bgcolor: 'primary.main',
                  fontSize: '2.5rem',
                  border: isDragging ? '3px dashed' : '3px solid',
                  borderColor: isDragging ? 'primary.main' : 'divider',
                  boxShadow: isDragging ? 6 : 3,
                  transition: 'all 0.3s'
                }}
              >
                {!user?.avatar && !user?.avatarUrl && formData.name.charAt(0).toUpperCase()}
              </Avatar>
              
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              
              {/* Camera button */}
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleAvatarClick();
                }}
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  bgcolor: 'primary.main',
                  color: 'white',
                  border: '2px solid',
                  borderColor: 'background.paper',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                    transform: 'scale(1.1)'
                  },
                  transition: 'all 0.2s'
                }}
                size="small"
                disabled={isUploading}
              >
                {isUploading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <PhotoCameraIcon fontSize="small" />
                )}
              </IconButton>
            </Box>
            
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PhotoCameraIcon />}
                onClick={handleAvatarClick}
                disabled={isUploading}
              >
                {t('profile.changePicture', 'Change Picture')}
              </Button>
              
              {(user?.avatar || user?.avatarUrl) && (
                <Button
                  variant="text"
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleRemoveAvatar}
                  disabled={isUploading}
                >
                  {t('profile.removePicture', 'Remove Picture')}
                </Button>
              )}
              
              <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 0.5 }}>
                {t('profile.avatarHint', 'JPG, PNG, GIF or WebP. Max 10MB.')}
              </Typography>
              <Typography variant="caption" color="primary" align="center" fontWeight={500}>
                {t('profile.dragDropHint', 'Click or drag & drop to upload')}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Form Fields */}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('profile.name', 'Name')}
                value={formData.name}
                onChange={handleChange('name')}
                error={!!errors.name}
                helperText={errors.name}
                disabled={isSubmitting}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('profile.email', 'Email')}
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                error={!!errors.email}
                helperText={errors.email || t('profile.emailNote', 'Email changes require verification')}
                disabled={isSubmitting}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label={t('profile.timezone', 'Timezone')}
                value={formData.timezone}
                onChange={handleChange('timezone')}
                disabled={isSubmitting}
                SelectProps={{
                  native: true,
                }}
              >
                {timezones.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label={t('profile.locale', 'Language')}
                value={formData.locale}
                onChange={handleChange('locale')}
                disabled={isSubmitting}
                SelectProps={{
                  native: true,
                }}
              >
                {locales.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </TextField>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/settings')}
              disabled={isSubmitting}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
              disabled={isSubmitting}
            >
              {isSubmitting ? t('common.saving', 'Saving...') : t('common.save', 'Save Changes')}
            </Button>
          </Box>
        </form>
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Avatar Upload Preview Dialog */}
      <Dialog
        open={uploadDialog}
        onClose={handleUploadCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {t('profile.previewAvatar', 'Preview Profile Picture')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
            {/* Preview Image */}
            <Avatar
              src={avatarPreview}
              sx={{
                width: 150,
                height: 150,
                mb: 2,
                border: '3px solid',
                borderColor: 'divider',
                boxShadow: 3
              }}
            />
            
            {/* File Info */}
            {selectedFile && (
              <Box sx={{ width: '100%', mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>{t('profile.fileName', 'File name:')}</strong> {selectedFile.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>{t('profile.fileSize', 'File size:')}</strong> {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>{t('profile.fileType', 'File type:')}</strong> {selectedFile.type}
                </Typography>
              </Box>
            )}

            {/* Upload Progress */}
            {isUploading && (
              <Box sx={{ width: '100%', mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('profile.uploading', 'Uploading...')}
                  </Typography>
                  <Typography variant="body2" color="primary" fontWeight={600}>
                    {uploadProgress}%
                  </Typography>
                </Box>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleUploadCancel}
            disabled={isUploading}
            startIcon={<CloseIcon />}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleUploadConfirm}
            variant="contained"
            disabled={isUploading}
            startIcon={isUploading ? <CircularProgress size={20} /> : <CheckIcon />}
          >
            {isUploading ? t('profile.uploading', 'Uploading...') : t('profile.uploadAvatar', 'Upload')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProfileSettings;
